import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Device, DeviceAppCycleMode, DeviceHistory, Milliseconds, toTitleCase, } from '@iotv/datamodel-core';
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import { Button, ButtonGroup, Card, Divider, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import Confirmation from '../../../../Popup/Confirmation';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { DeviceControlDAO } from '../../../../data/DeviceControlDAO';
import { Severity } from '../../../../types';
import { DeviceControlContext } from '../../../../v2/components/generic/Control/LFC-Devices/DeviceControlContext';
import { AutosizedTextField } from '../../../common/AutosizedTextField';
import { DeviceConfigContext } from '../../Context/deviceConfigContext';
/** **IoTV limitation**: Devices may not respond to 3, 4, 5 properly! */
const datarates = [0, 1, 2, 3, 4, 5];
const DEFAULT = 'Default';
const ConfigActionTable = ({ device, config }) => {
    const [{ contextUser, UserFunctions, isSmallView }] = useContext(AppMainViewContext);
    const { state: { deviceHistory }, dispatch, } = useContext(DeviceConfigContext);
    const { state: { supportsABE }, } = useContext(DeviceControlContext);
    const inferredAppCycleTime = isNonNaNNumber(device.lastSendPeriod) ? Math.round(device.lastSendPeriod / Milliseconds.MINUTE) : undefined;
    const inferredOrReportedAppCycleTime = config?.reported?.appCycleTime ?? inferredAppCycleTime;
    const inferredOrReportedAppCycleMode = (config?.reported?.appCycleMode ?? [0, 1].includes(inferredOrReportedAppCycleTime ?? Infinity))
        ? DeviceAppCycleMode['Rapid Response Mode']
        : DeviceAppCycleMode.Normal;
    const [appCycleMode, setAppCycleMode] = useState(inferredOrReportedAppCycleMode);
    const [appCycleTime, setAppCycleTime] = useState(inferredOrReportedAppCycleTime);
    const [sendingApp, setSendingApp] = useState(false);
    const [sysCycleTime, setSysCycleTime] = useState(config?.reported?.sysCycleTime ?? device.sysCycleTime);
    const [sysInvalid, setSysInvalid] = useState(true);
    const [sendingSys, setSendingSys] = useState(false);
    const [randomizationFactor, setRandomizationFactor] = useState(device.random);
    const [randomInvalid, setRandomInvalid] = useState(true);
    const [sendingRandom, setSendingRandom] = useState(false);
    const [adr, setADR] = useState(device.adr ?? true);
    const [sendingADR, setSendingADR] = useState(false);
    const [dr, setDR] = useState(device.dr);
    const [sendingDR, setSendingDR] = useState(false);
    const [bytes, setBytes] = useState('');
    const [sendingBytes, setSendingBytes] = useState(false);
    const [deviceRestartMode, setDeviceRestartMode] = useState();
    const [confirmation, setConfirmation] = useState(false);
    const [sendingRestart, setSendingRestart] = useState(false);
    const sending = sendingApp || sendingSys || sendingRandom || sendingADR || sendingBytes || sendingRestart;
    const sendDeviceTransaction = async (attribute) => {
        const desiredPayload = {};
        const newConfigMessage = {
            attribute: '',
            from: '',
            to: '',
            user: contextUser?.name ?? contextUser?.id ?? 'Unknown',
            timestamp: Date.now(),
        };
        const clone = new Device(device);
        switch (attribute) {
            case 'ACM':
                if (appCycleMode) {
                    setSendingApp(true);
                    desiredPayload.appCycleTime = appCycleTime;
                    newConfigMessage.attribute = 'App Cycle Mode';
                    newConfigMessage.from = device?.appCycleMode ?? DEFAULT;
                    newConfigMessage.to = appCycleMode.toLocaleString();
                    clone.appCycleMode = appCycleMode;
                    clone.appCycleTime = inferredOrReportedAppCycleTime;
                }
                break;
            case 'ACT':
                if (appCycleTime) {
                    setSendingApp(true);
                    desiredPayload.appCycleTime = appCycleTime;
                    newConfigMessage.attribute = 'App Cycle Time';
                    newConfigMessage.from = device?.appCycleTime?.toString() ?? DEFAULT;
                    newConfigMessage.to = appCycleTime.toLocaleString();
                    clone.appCycleMode = appCycleMode;
                    clone.appCycleTime = appCycleTime;
                }
                break;
            case 'SCT':
                if (sysCycleTime) {
                    setSendingSys(true);
                    desiredPayload.appCycleTime = sysCycleTime;
                    newConfigMessage.attribute = 'System Cycle Time';
                    newConfigMessage.from = device?.sysCycleTime?.toString() ?? DEFAULT;
                    newConfigMessage.to = sysCycleTime.toLocaleString();
                    clone.sysCycleTime = sysCycleTime;
                }
                break;
            case 'RV':
                if (randomizationFactor) {
                    setSendingRandom(true);
                    desiredPayload.randomizationFactor = randomizationFactor;
                    newConfigMessage.attribute = 'Random Value';
                    newConfigMessage.from = device?.random?.toString() ?? DEFAULT;
                    newConfigMessage.to = randomizationFactor.toLocaleString();
                    clone.random = randomizationFactor;
                }
                break;
            case 'ADR':
                setSendingADR(true);
                desiredPayload.adr = adr ? 1 : 0;
                newConfigMessage.attribute = 'Adaptive Data Rate';
                newConfigMessage.from = device?.adr?.toString() ?? DEFAULT;
                newConfigMessage.to = adr.toLocaleString();
                break;
            case 'DR':
                if (dr !== undefined) {
                    setSendingDR(true);
                    desiredPayload.dr = dr;
                    newConfigMessage.attribute = 'Data Rate';
                    newConfigMessage.from = device?.dr?.toString() ?? DEFAULT;
                    newConfigMessage.to = dr.toLocaleString();
                }
                break;
            case 'REBOOT':
                setSendingRestart(true);
                desiredPayload.reboot = 0xff5a;
                newConfigMessage.attribute = 'Reboot';
                break;
            case 'RESET':
                setSendingRestart(true);
                desiredPayload.restoreDefaults = 0;
                newConfigMessage.attribute = 'All Configs';
                newConfigMessage.to = DEFAULT;
                clone.appCycleTime = undefined;
                clone.sysCycleTime = undefined;
                clone.random = undefined;
                clone.adr = undefined;
                clone.dr = undefined;
                break;
        }
        deviceHistory?.appendConfig(newConfigMessage);
        const dct = {
            desiredPayload,
            devices: [device],
            requestInitiator: contextUser,
        };
        const transactionPromise = DeviceControlDAO.device.control.command.send(dct);
        const historyPromise = deviceHistory !== null ? ConsumerDao.save(deviceHistory) : new Promise((resolve) => resolve({ err: null, data: null }));
        const savePromise = ConsumerDao.save(clone);
        Promise.all([transactionPromise, historyPromise, savePromise]).then(([_, history, dev]) => {
            switch (attribute) {
                case 'ACM':
                    setSendingApp(false);
                    break;
                case 'ACT':
                    setSendingApp(false);
                    break;
                case 'SCT':
                    setSendingSys(false);
                    break;
                case 'RV':
                    setSendingRandom(false);
                    break;
                case 'ADR':
                    setSendingADR(false);
                    break;
                case 'DR':
                    setSendingDR(false);
                    break;
                case 'REBOOT':
                    setSendingRestart(false);
                    break;
                case 'RESET':
                    setSendingRestart(false);
                    break;
            }
            if (history.err === null && history.data !== null)
                dispatch({ deviceHistory: new DeviceHistory(history.data) });
            if (dev.err === null && dev.data !== null)
                dispatch({ device: new Device(dev.data) });
        });
    };
    const sendDevicePayloadBytes = async () => {
        if (device == null)
            return;
        setSendingBytes(true);
        const { err, data } = await DeviceControlDAO.device.payload.send({ device, bytes });
        if (err || !data) {
            UserFunctions.setUserMessage({
                id: 'sendDevicePayloadBytes',
                type: 'MessageBar',
                severity: Severity.error,
                label: 'Error sending device payload bytes',
                content: err ? err.message : 'Failed to send!',
            });
        }
        setSendingBytes(false);
        setBytes('');
    };
    return (_jsxs(Card, { elevation: 1, children: [_jsx(Stack, { padding: 2, direction: "row", style: { backgroundColor: 'lightgray' }, children: _jsx(Typography, { variant: "h6", children: "Change Configuration" }) }), _jsxs(Stack, { width: "100%", sx: { overflowX: 'auto' }, children: [_jsxs(Row, { children: [_jsx(Title, { children: "App Cycle Mode" }), _jsxs(ButtonGroup, { children: [_jsx(Button, { variant: DeviceAppCycleMode.Normal === appCycleMode || appCycleMode === undefined ? 'contained' : 'outlined', disabled: sending || sendingApp, onClick: () => {
                                            setAppCycleMode(DeviceAppCycleMode.Normal);
                                            setAppCycleTime(device.appCycleTime);
                                        }, children: "Normal" }), _jsx(Button, { variant: DeviceAppCycleMode['Rapid Response Mode'] === appCycleMode ? 'contained' : 'outlined', disabled: sending || sendingApp, onClick: () => {
                                            setAppCycleMode(DeviceAppCycleMode['Rapid Response Mode']);
                                            setAppCycleTime(1);
                                        }, children: !isSmallView ? 'Rapid Response' : 'Rapid' })] }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('ACM'), disabled: appCycleMode === undefined || appCycleMode !== DeviceAppCycleMode['Rapid Response Mode'] || sending || sendingApp, loading: sendingApp, children: "Send" })] }), _jsx(Divider, {}), appCycleMode !== DeviceAppCycleMode['Rapid Response Mode'] && (_jsxs(_Fragment, { children: [_jsxs(Row, { children: [_jsx(Title, { children: "App Cycle Time" }), _jsx(AutosizedTextField, { placeholder: "1 ~ 1,440", value: appCycleTime, onChange: (e) => {
                                            const val = parseInt(e.target.value);
                                            if (val) {
                                                setAppCycleTime(Math.min(Math.max(1, val), 1440));
                                            }
                                            else if (e.target.value === '') {
                                                setAppCycleTime(undefined);
                                            }
                                        }, disabled: sending || sendingApp, slotProps: {
                                            input: {
                                                endAdornment: _jsx(InputAdornment, { position: "end", children: "minutes" }),
                                            },
                                        } }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('ACT'), disabled: !isNonNaNNumber(appCycleTime) || sending || sendingApp, loading: sendingApp, children: "Send" })] }), _jsx(Divider, {})] })), _jsxs(Row, { children: [_jsx(Title, { children: "System Cycle Time" }), _jsx(AutosizedTextField, { placeholder: "60 ~ 10,080", value: sysCycleTime, onChange: (e) => {
                                    let val = parseInt(e.target.value);
                                    if (val) {
                                        val = Math.min(Math.max(60, val), 10800);
                                        setSysCycleTime(val);
                                    }
                                    else if (e.target.value === '') {
                                        setSysCycleTime(undefined);
                                    }
                                    setSysInvalid(val === undefined || isNaN(val) || val < 60 || val > 10800);
                                }, disabled: sending || sendingSys, slotProps: {
                                    input: {
                                        endAdornment: _jsx(InputAdornment, { position: "end", children: "minutes" }),
                                    },
                                } }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('SCT'), disabled: sysInvalid || sending || sendingSys, loading: sendingSys, children: "Send" })] }), _jsx(Divider, {}), _jsxs(Row, { children: [_jsx(Title, { children: "Randomisation Value" }), _jsx(AutosizedTextField, { placeholder: "5,000 ~ 60,000", value: randomizationFactor, onChange: (e) => {
                                    let val = parseInt(e.target.value);
                                    if (val) {
                                        val = Math.min(val, 60000);
                                        setRandomizationFactor(val);
                                    }
                                    else if (e.target.value === '') {
                                        setRandomizationFactor(undefined);
                                    }
                                    setRandomInvalid(val === undefined || isNaN(val) || val < 5000 || val > 60000);
                                }, disabled: sending || sendingRandom, slotProps: {
                                    input: {
                                        endAdornment: _jsx(InputAdornment, { position: "end", children: "milliseconds" }),
                                    },
                                } }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('RV'), disabled: randomInvalid || sending || sendingRandom, loading: sendingRandom, children: "Send" })] }), _jsx(Divider, {}), !supportsABE && (_jsxs(Row, { children: [_jsx(Title, { children: "Adaptive Data Rate" }), _jsxs(ButtonGroup, { children: [_jsx(Button, { variant: !adr ? 'contained' : 'outlined', onClick: () => setADR(false), disabled: sending || sendingADR, children: "Off" }), _jsx(Button, { variant: adr ? 'contained' : 'outlined', onClick: () => setADR(true), disabled: sending || sendingADR, children: "On" })] }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('ADR'), disabled: sending || sendingADR, loading: sendingADR, children: "Send" })] })), _jsx(Divider, {}), _jsxs(Row, { children: [_jsx(Title, { children: "Data Rate" }), _jsx(ButtonGroup, { sx: { flexWrap: isSmallView ? 'wrap' : 'nowrap' }, children: datarates.map((r) => {
                                    return (_jsx(Tooltip, { title: `DR${r} (SF ${12 - r})`, arrow: true, placement: "top", slotProps: { popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }, children: _jsx("span", { children: _jsxs(Button, { variant: dr === r ? 'contained' : 'outlined', onClick: () => setDR(r), disabled: sending || sendingDR, children: ["DR", r] }) }) }, r));
                                }) }), _jsx(Button, { variant: "outlined", onClick: () => sendDeviceTransaction('DR'), disabled: sending || sendingDR, loading: sendingDR, children: "Send" })] }), _jsx(Divider, {}), _jsxs(Row, { children: [_jsx(Title, { children: "Payload" }), _jsx(AutosizedTextField, { placeholder: "bytes...", value: bytes?.replace(/([0-9a-fA-F]{2})(?!=(\s|$))/g, '$1 ').trimEnd() ?? '', onChange: (e) => setBytes(e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '')), disabled: sending || sendingBytes, slotProps: {
                                    input: {
                                        startAdornment: _jsxs(InputAdornment, { position: "start", children: ["0x", device?.deviceType?.toString(16).toUpperCase()] }),
                                    },
                                } }), _jsx(Button, { variant: "outlined", onClick: sendDevicePayloadBytes, disabled: bytes.length === 0 || bytes.length % 2 === 1 || sending || sendingBytes, loading: sendingBytes, children: "Send" })] }), _jsx(Divider, {}), _jsxs(Row, { children: [_jsx(Title, { children: "Device" }), _jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(Tooltip, { title: "Reboot Device", arrow: true, children: _jsx(Button, { color: "error", variant: "outlined", onClick: () => {
                                                setDeviceRestartMode('REBOOT');
                                                setConfirmation(true);
                                            }, disabled: sending || sendingRestart, loading: deviceRestartMode === 'REBOOT' && sendingRestart, children: "Reboot" }) }), _jsx(Tooltip, { title: "Reset Device to Factory Settings", arrow: true, children: _jsx(Button, { color: "error", variant: "outlined", onClick: () => {
                                                setDeviceRestartMode('RESET');
                                                setConfirmation(true);
                                            }, disabled: sending || sendingRestart, loading: deviceRestartMode === 'RESET' && sendingRestart, children: "Reset" }) })] })] })] }), _jsx(Confirmation, { name: `Confirm ${deviceRestartMode?.toLocaleLowerCase()}`,
                message: `Are you sure you would like to ${deviceRestartMode?.toLocaleLowerCase()} this device?${deviceRestartMode === 'RESET' ? '\nWARNING: factory reset!' : ''}`,
                state: confirmation,
                changeState: setConfirmation,
                action: toTitleCase(deviceRestartMode),
                actionFunction: () => {
                    deviceRestartMode !== undefined && sendDeviceTransaction(deviceRestartMode);
                    setDeviceRestartMode(undefined);
                } })] }));
};
export default ConfigActionTable;
const Row = ({ children }) => {
    const [first, ...rest] = children;
    return (_jsxs(Stack, { minHeight: "2.5em", padding: 0.5, direction: "row", flexGrow: 1, justifyContent: "space-between", alignItems: "center", gap: 1, children: [_jsx(Stack, { flexGrow: 1, maxWidth: "10em", direction: "row", justifyContent: "flex-start", children: first }), rest] }));
};
const Title = ({ children }) => (_jsx(Typography, { variant: "body2", paddingInlineStart: 1, flexGrow: 1, children: children }));
